import { useMemo, useState, type CSSProperties } from 'react';
import { useAppSelector } from '../../store';
import { testIds } from '../../lib/testIds';
import { DiffToggle } from '../timeline/DiffToggle';
import { TimelineScrubber } from '../timeline/TimelineScrubber';
import { SideBySideDiff } from '../timeline/SideBySideDiff';
import { UnifiedDiff } from '../timeline/UnifiedDiff';
import { ForkControls } from '../timeline/ForkControls';
import type { TimelineFork } from '@repancy/core';

interface TimelinePanelProps {
  sessionId: string;
}

const wrapperStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderTop: '1px solid #808080',
  marginTop: 8,
  fontFamily: 'var(--xp-font-body)',
  fontSize: 12,
  color: '#000',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 8px',
  background: '#ece9d8',
  cursor: 'pointer',
  borderBottom: '1px solid #808080',
};

const summaryStyle: CSSProperties = {
  flex: 1,
  fontWeight: 'bold',
};

const countStyle: CSSProperties = {
  color: '#404040',
  fontWeight: 'normal',
};

const bodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  background: '#ece9d8',
};

const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 8px',
  borderBottom: '1px solid #808080',
  flexWrap: 'wrap',
};

const selectStyle: CSSProperties = {
  fontFamily: 'var(--xp-font-body)',
  fontSize: 11,
  padding: '1px 4px',
  border: '2px solid',
  borderColor: '#808080 #dfdfdf #dfdfdf #808080',
  background: '#fff',
};

const tabBarStyle: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid #808080',
};

const tabButton = (active: boolean): CSSProperties => ({
  fontFamily: 'var(--xp-font-body)',
  fontSize: 11,
  padding: '4px 12px',
  cursor: 'pointer',
  border: '1px solid #808080',
  borderBottom: active ? '1px solid #ece9d8' : '1px solid #808080',
  backgroundColor: active ? '#ece9d8' : '#b0b0b0',
  marginBottom: active ? -1 : 0,
  fontWeight: active ? 'bold' : 'normal',
  outline: 'none',
});

const contentStyle: CSSProperties = {
  padding: '0 8px 8px',
  maxHeight: 320,
  overflow: 'auto',
};

const emptyStateStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 96,
  color: '#606060',
  fontStyle: 'italic',
  border: '2px solid',
  borderColor: '#808080 #dfdfdf #dfdfdf #808080',
  backgroundColor: '#fff',
  margin: 8,
};

type ActiveTab = 'diff' | 'forks';

export function TimelinePanel({ sessionId }: TimelinePanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('forks');
  const [selectedForkId, setSelectedForkId] = useState<string | null>(null);

  const viewMode = useAppSelector((s) => s.diff.viewMode);
  const reduxForkIds = useAppSelector((s) => s.timelines.forks.ids);
  const reduxForkEntities = useAppSelector((s) => s.timelines.forks.entities);
  const stepIds = useAppSelector((s) => s.timelines.steps.ids);
  const stepEntities = useAppSelector((s) => s.timelines.steps.entities);

  const forks = useMemo<TimelineFork[]>(() => {
    const all: TimelineFork[] = [];
    for (const id of reduxForkIds) {
      const f = reduxForkEntities[id];
      if (f) all.push(f);
    }
    if (!sessionId) return all;
    return all.filter(
      (f) => f.parentSessionId === sessionId || f.forkedSessionId === sessionId,
    );
  }, [reduxForkIds, reduxForkEntities, sessionId]);

  const stepsForSession = useMemo(() => {
    return stepIds
      .map((id) => stepEntities[id])
      .filter((st) => !!st && st.sessionId === sessionId);
  }, [stepIds, stepEntities, sessionId]);

  const hasForks = forks.length > 0;
  const hasSteps = stepsForSession.length > 0;

  const selectedFork = useMemo(
    () => forks.find((f) => f.id === selectedForkId) ?? forks[0] ?? null,
    [forks, selectedForkId],
  );

  const forkStepIndices = useMemo(() => forks.map((f) => f.forkStepIndex), [forks]);

  const handleViewDiff = (fork: TimelineFork) => {
    setSelectedForkId(fork.id);
    setActiveTab('diff');
  };

  return (
    <div style={wrapperStyle} data-testid={testIds.promptLab.timelinePanel}>
      <div
        style={headerStyle}
        onClick={() => setExpanded((v) => !v)}
        title="Steps from each Run land here. Use Fork Here to branch the conversation."
        data-testid={testIds.promptLab.timelineToggle}
      >
        <span style={{ width: 12 }}>{expanded ? '▼' : '▶'}</span>
        <span style={summaryStyle}>Timeline &amp; Forks</span>
        <span style={countStyle}>
          {stepsForSession.length} step{stepsForSession.length === 1 ? '' : 's'}
          {' · '}
          {forks.length} fork{forks.length === 1 ? '' : 's'}
        </span>
      </div>
      {expanded && (
        <div style={bodyStyle}>
          <div style={toolbarStyle}>
            <DiffToggle />
            <div style={{ flex: 1 }} />
            {hasForks && forks.length > 1 && (
              <select
                style={selectStyle}
                value={selectedFork?.id ?? ''}
                onChange={(e) => setSelectedForkId(e.target.value)}
                data-testid={testIds.promptLab.timelineForkSelect}
              >
                {forks.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {hasSteps && (
            <div style={{ padding: '4px 0' }}>
              <TimelineScrubber
                sessionId={sessionId}
                forkStepIndices={forkStepIndices}
              />
            </div>
          )}

          <div style={tabBarStyle}>
            <button
              type="button"
              style={tabButton(activeTab === 'forks')}
              onClick={() => setActiveTab('forks')}
              title="See every fork on this session and resume any of them."
              data-testid={testIds.promptLab.timelineTabForks}
            >
              Forks ({forks.length})
            </button>
            <button
              type="button"
              style={tabButton(activeTab === 'diff')}
              onClick={() => setActiveTab('diff')}
              title="Compare the active fork against the parent session, side-by-side or unified."
              data-testid={testIds.promptLab.timelineTabDiff}
            >
              Compare
            </button>
          </div>

          <div style={contentStyle}>
            {activeTab === 'forks' && (
              <ForkControls
                sessionId={sessionId}
                forks={forks}
                onViewDiff={handleViewDiff}
              />
            )}

            {activeTab === 'diff' && !hasForks && (
              <div style={emptyStateStyle}>
                Fork a session to compare outputs
              </div>
            )}

            {activeTab === 'diff' && hasForks && selectedFork && (
              <>
                {viewMode === 'side-by-side' ? (
                  <SideBySideDiff
                    originalSessionId={selectedFork.parentSessionId}
                    forkedSessionId={selectedFork.forkedSessionId}
                  />
                ) : (
                  <UnifiedDiff
                    originalSessionId={selectedFork.parentSessionId}
                    forkedSessionId={selectedFork.forkedSessionId}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
